import uuid from 'uuid/v4';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';

import { type ExperienceEvent, type ExperienceAttributes, hasAttribute } from './ExperienceEvent';
import { collectAll } from './ExperienceEventCollector';
import {
	CREATE_INLINE_COMMENT_EXPERIENCE,
	RESOLVE_INLINE_COMMENT_EXPERIENCE,
	SELECT_INLINE_COMMENT_EXPERIENCE,
	SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
	SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
	VIEW_INLINE_COMMENT_EXPERIENCE,
} from './ExperienceName';
import { getExperienceTracker } from './getExperienceTracker';
import { startUFOPressTrace } from './startExperienceForRoute';

export type SelectInlineCommentCompoundExperienceEntryPoint =
	/**
	 * Used when a user creates a new comment by highlighting text and clicking the create comment button.
	 * Triggered when a new inline comment is successfully saved.
	 */
	| 'create-comment'
	/**
	 * Used when a user clicks on an annotation in the document to select an existing comment.
	 * Triggered when annotation selection is completed in the editor.
	 */
	| 'annotation-change'
	/**
	 * Used when a user navigates between comments using the previous/next buttons in the comment sidebar.
	 * Triggered by clicking navigation arrows in the CommentNavigation component.
	 */
	| 'comment-navigation'
	/**
	 * Used when a user navigates between comments using keyboard shortcuts.
	 * Triggered by keyboard shortcut listeners for next/previous comment navigation.
	 */
	| 'keyboard-navigation'
	/**
	 * Used when a user selects a comment from the comments panel.
	 * Default entry point for panel-based comment selections.
	 */
	| 'sidebar'
	/**
	 * Fallback entry point when renderer focuses on an annotation.
	 * Used when a user clicks on an annotation in the document to select an existing comment.
	 * Triggered when annotation selection is completed in the renderer.
	 */
	| 'unknown:RendererFocus'
	/**
	 * Fallback entry point for inline comment selections in the document with unknown trigger.
	 * Used as a generic fallback when the selection mechanism is not explicitly tracked.
	 */
	| 'unknown:InlineComment'
	/**
	 * Fallback entry point for inline comment selections in the comments panel with unknown trigger.
	 * Specifically used when keyboard navigation is followed by comments panel selection.
	 */
	| 'unknown:CommentsPanelList';

type AnnotationIdKnownStartAttributes = {
	pageClass: 'editor' | 'renderer' | 'not-page-or-doc';
	/**
	 * This is optional as in some scenarios the entry point does not know
	 * what type of comment is being selected (e.g. comment navigation, sidebar).
	 **/
	commentType?: 'block' | 'inline';
	blockType?: 'media';
	annotationId: string;
	entryPoint:
		| 'create-comment'
		| 'annotation-change'
		| 'sidebar'
		| 'unknown:RendererFocus'
		| 'unknown:InlineComment'
		| 'unknown:CommentsPanelList';
};
type AnnotationIdUnknownStartAttributes = {
	pageClass: 'editor' | 'renderer' | 'not-page-or-doc';
	/**
	 * This is optional as in some scenarios the entry point does not know
	 * what type of comment is being selected (e.g. comment navigation, sidebar).
	 **/
	commentType?: 'block' | 'inline';
	blockType?: 'media';
	annotationId?: undefined;
	entryPoint: 'keyboard-navigation' | 'comment-navigation';
};
export type StartAttributes = AnnotationIdKnownStartAttributes | AnnotationIdUnknownStartAttributes;

let runningExperiences: string[] = [];
let commonSelectInlineCommentExperiencesAttributes: ExperienceAttributes = {};

function getSelectInlineCommentExperienceDebug(experienceName: string, experienceStage?: string) {
	function createInlineCommentExperienceDebug({
		error,
		createAnalyticsEvent,
		extraAttributes,
	}: {
		createAnalyticsEvent: CreateUIAnalyticsEvent;
		error: Error;
		extraAttributes?: ExperienceAttributes;
	}) {
		const experienceTracker = getExperienceTracker();

		const experienceAttributes = experienceTracker.getExperienceState(
			SELECT_INLINE_COMMENT_EXPERIENCE,
		);
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'error',
				actionSubject: SELECT_INLINE_COMMENT_EXPERIENCE,
				source: experienceName,
				attributes: {
					experienceStage,
					...experienceAttributes,
					...commonSelectInlineCommentExperiencesAttributes,
					...extraAttributes,

					error: error.message || String(error),
					stack: error.stack,
				},
			},
		}).fire();
	}
	return createInlineCommentExperienceDebug;
}

enum ExperienceType {
	DOCUMENT = 'document',
	DOCUMENT_WITH_PANEL = 'documentWithPanel',
	PANEL = 'panel',
}

function getExperienceType(
	entryPoint: SelectInlineCommentCompoundExperienceEntryPoint,
	sidebarOpenAtStart: boolean,
): ExperienceType {
	switch (entryPoint) {
		case 'create-comment':
		case 'comment-navigation':
		case 'unknown:InlineComment':
			return ExperienceType.DOCUMENT;
		case 'annotation-change':
		case 'keyboard-navigation':
		case 'unknown:RendererFocus':
			return sidebarOpenAtStart ? ExperienceType.DOCUMENT_WITH_PANEL : ExperienceType.DOCUMENT;
		case 'sidebar':
		case 'unknown:CommentsPanelList':
			return ExperienceType.PANEL;
		default:
			return ExperienceType.DOCUMENT;
	}
}

/**
 * Comment selection can occur in a variety of ways;
 * - with sidebar open
 *   - select a document annotation
 *   - select a comment in the sidebar
 *   - select a subsequent comment in the sidebar (different logic is used between first and subsequent sidebar selections)
 * - with sidebar closed
 *   - select a document annotation
 *   - using the keyboard or ui next/previous navigations
 *   - via loading a page with a comment in the query string
 * - via notifications
 *
 * The experience tracker has a number of constraints;
 *
 * At the start of the experience, the "show" and "select" can be in different orders
 *  - sidebar vs panel vs annotation selection entrypoints
 * To accommodate this – the parent experience does not have an explicit start, and will be started when either the select or show sub experiences start.
 * To avoid restarts – when starting the parent experience – we check if there is an experience running with the same annotation id, and if so do nothing (ie. don't start a new one).
 *
 * At the start of the experience, the annotationId is not always known
 * - panel next/previous navigation
 * When a start occurs in this scenario, we always restart the experience
 *
 * For some experience the first time we get a reliable signal that an annotation has been selected – is when the selection has completed
 * - annotation based selections and un wired product selections (at time of writing, notifications)
 * To accommodate this – on selection complete – we check if there is an active select experience, and if not start one.
 */
// IMPORTANT When cleaning up -- the singleExperience version of this compound experience should have the following changes
// - split the selectAnnotation.start into distinct signals for when annotationId is known and when it is not
// - update the naming of the singleExperience sub experiences to make it clear that they are for signals rather than sub experiences
const multiExperience_selectInlineCommentCompoundExperience = {
	/**
	 * Intended for internal use only.
	 *
	 * When the selectAnnotation and selectAnnotation are started they
	 * fire this with the start properties.
	 */
	_start: (startAttributes: StartAttributes, _options?: { annotationUnknown: true }) => {
		const experienceTracker = getExperienceTracker();
		const runningExperienceAttributes = experienceTracker.getExperienceState(
			SELECT_INLINE_COMMENT_EXPERIENCE,
		)?.attributes;

		const skipStart = shouldSkipStarting({
			startAttributes,
			experienceAttributes: runningExperienceAttributes,
		});
		// If there is an existing experience tracker for the same annotationId
		// we assume the experience is still active, and don't start a new one.
		// Note: for keyboard/comment navigation, we don't get an annotationId on the startAttributes
		// so this will always be false. They set the annotationId in synchronous code following the start,
		// so it should still be set prior to any additional start events.
		if (skipStart) {
			// However -- if the second start has a more specific entry point
			// we update the entry point attribute of the existing experience.
			if (
				(runningExperienceAttributes?.entryPoint as string | undefined)?.startsWith('unknown') &&
				!startAttributes.entryPoint.startsWith('unknown')
			) {
				selectInlineCommentCompoundExperience.addCommonAttributes({
					entryPoint: startAttributes.entryPoint,
				});
			}

			return;
		}
		if (
			// When the side bar is open, and the user selects a comment using the keyboard,
			// the keyboard navigation experience is first (when the annotationId is not known),
			// followed by the show comment experience
			startAttributes.entryPoint === 'unknown:CommentsPanelList' &&
			runningExperienceAttributes?.entryPoint === 'keyboard-navigation' &&
			runningExperienceAttributes?.annotationId === undefined
		) {
			selectInlineCommentCompoundExperience.addCommonAttributes({
				annotationId: startAttributes.annotationId,
			});
			return;
		}

		// First prepare the new attributes without modifying the global state
		// This ensures that any abort of current experiences will use their original compoundTaskId
		const newAttributes: ExperienceAttributes = {
			pageClass: startAttributes.pageClass,
			commentType: startAttributes.commentType,
			annotationId: 'annotationId' in startAttributes ? startAttributes.annotationId : undefined,
			entryPoint: startAttributes.entryPoint,
			version: 'multi-experience',
		};

		// We check if there is a dom element with a testId of "object-sidebar-panel-body" and if so mark the
		// experience as being during a sidebar open experience.
		if (fg('confluence_comments_select_comment_last_click')) {
			newAttributes.sidebarOpenAtStart =
				document.querySelector('[data-testid="object-sidebar-panel-body"]') !== null;
		}

		if (fg('confluence_frontend_split_select_comment_exp')) {
			newAttributes.experienceType = getExperienceType(
				startAttributes.entryPoint,
				newAttributes.sidebarOpenAtStart as boolean,
			);
		}

		// If the create inline comment experience is running, we want to link the two experiences
		const createInlineCommentExperienceState = experienceTracker.getExperienceState(
			CREATE_INLINE_COMMENT_EXPERIENCE,
		);

		if (createInlineCommentExperienceState && !createInlineCommentExperienceState.hasStopped) {
			newAttributes.linkedCompoundTaskId =
				createInlineCommentExperienceState.attributes?.compoundTaskId;
		}

		// Create a new compoundTaskId for the selectInlineComment experience
		const compoundTaskId = uuid();
		newAttributes.compoundTaskId = compoundTaskId;

		if (fg('confluence_frontend_comments_exp_trace_id')) {
			// Start press trace if no trace is currently active
			startUFOPressTrace(SELECT_INLINE_COMMENT_EXPERIENCE);
		}

		// Then start a new compound experience with the new attributes
		// This will abort any running experiences with the same name, but they'll keep their original attributes
		experienceTracker.start({
			name: SELECT_INLINE_COMMENT_EXPERIENCE,
			id: compoundTaskId,
			attributes: newAttributes,
			// If the annotation is not selected and comment shown to the user within 12 seconds -- we assume there has been a failure to select the inline comment
			timeout: 12000,

			collect: (events, experience) => {
				collectAll([
					SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
					SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
				])(events, experience);

				const isThisCommentBeingResolved = (event: ExperienceEvent) => {
					return (
						event.name === RESOLVE_INLINE_COMMENT_EXPERIENCE &&
						event.action === 'taskStart' &&
						hasAttribute(
							event,
							'annotationId',
							commonSelectInlineCommentExperiencesAttributes.annotationId,
						)
					);
				};
				events.forEach((event) => {
					if (isThisCommentBeingResolved(event)) {
						selectInlineCommentCompoundExperience.abort({
							abortReason: 'Comment being resolved',
						});
					}
				});
			},
		});

		// Now it's safe to update the global state since any aborts have already happened
		// We reset experience state if we are starting a new experience
		runningExperiences = [SELECT_INLINE_COMMENT_EXPERIENCE];
		commonSelectInlineCommentExperiencesAttributes = newAttributes;
	},
	addCommonAttributes: (additionalCommonAttributes: {
		[key: string]: boolean | null | number | string | undefined | Array<number | string>;
	}) => {
		const experienceTracker = getExperienceTracker();
		commonSelectInlineCommentExperiencesAttributes = {
			...commonSelectInlineCommentExperiencesAttributes,
			...additionalCommonAttributes,
		};
		for (const runningExperience of runningExperiences) {
			experienceTracker.updateAttributes({
				name: runningExperience,
				attributes: additionalCommonAttributes,
			});
		}
	},
	abort: ({
		abortReason,
	}: {
		abortReason:
			| 'test abort'
			| 'Comment being resolved'
			| 'Selected comment which has been resolved';
	}) => {
		const experienceTracker = getExperienceTracker();
		experienceTracker.abort({
			name: SELECT_INLINE_COMMENT_EXPERIENCE,
			reason: abortReason,
			checkForTimeout: true,
		});

		const subExperienceAbortReason =
			`Parent experience aborting with reason: ${abortReason}` as const;
		selectInlineCommentCompoundExperience.selectAnnotation.abort({
			abortReason: subExperienceAbortReason,
		});
		selectInlineCommentCompoundExperience.showComment.abort({
			abortReason: subExperienceAbortReason,
		});
	},
	selectAnnotation: {
		debug: getSelectInlineCommentExperienceDebug(
			SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
		),
		start: (startAttributes: StartAttributes) => {
			const experienceTracker = getExperienceTracker();
			selectInlineCommentCompoundExperience._start(startAttributes);
			const experienceAttributes = experienceTracker.getExperienceState(
				SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
			);

			const skipStart = shouldSkipStarting({
				startAttributes,
				experienceAttributes: experienceAttributes?.attributes,
			});
			// If the experience has already started for this annotation -- we don't start a new one
			// this can happen when the selectAnnotation is called after the showComment
			// (ie. when the user selects a comment in the sidebar)
			if (skipStart) {
				return;
			}
			experienceTracker.start({
				name: SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
				attributes: commonSelectInlineCommentExperiencesAttributes,
				// If the annotation does not complete selection within 10 seconds -- we assume there has been a failure to select the inline comment.
				timeout: 10000,
			});
			// The select experience can be started after the show experience
			// (ie. when the user selects a comment in the sidebar)
			// In this case we still want to track where the select experience started.
			selectInlineCommentCompoundExperience.addCommonAttributes({
				selectAnnotationEntryPoint: startAttributes.entryPoint,
			});
			runningExperiences.push(SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE);
		},
		abort: ({
			abortReason,
		}: {
			abortReason:
				| 'Comment navigation when only one comment'
				| 'Navigating to general comment'
				| 'Parent experience aborting with reason: test abort'
				| 'Parent experience aborting with reason: Comment being resolved'
				| 'Parent experience aborting with reason: Selected comment which has been resolved';
		}) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.abort({
				name: SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
				reason: abortReason,
			});
		},
		complete: (startAttributes: StartAttributes & { annotationId: string }) => {
			const experienceTracker = getExperienceTracker();

			// There can be cases where no start has been called for the select experience.
			// - When a product consumer of the editor setselectedannotation api calls the select experience
			//   and has not been instrumented.
			// - When the annotation selection is via editor logic (ie. editor selection being moved onto
			//   an annotation).
			const experienceState = experienceTracker.getExperienceState(
				SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
			);
			if (
				experienceState === null ||
				// When the experience state has stopped -- we assume it's from a previous experience
				experienceState.hasStopped === true
			) {
				selectInlineCommentCompoundExperience.selectAnnotation.start(startAttributes);
			}

			experienceTracker.succeed({
				name: SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
				attributes: commonSelectInlineCommentExperiencesAttributes,
			});

			// Temporarily start the view-inline-comment experience here for the editor entry point
			startViewInlineCommentExperience(startAttributes);
		},
	},
	showComment: {
		debug: getSelectInlineCommentExperienceDebug(SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE),
		debugPoint: (message: string, attributes?: ExperienceAttributes) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.attachDebugPoint({
				name: SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
				message,
				attributes,
			});
		},
		// The logic around starting the compound experience is opinionated about the showComment
		// experience always starting with an annotationId.
		// This is because it will prevent experience restarts where the showComment experience
		// is called multiple times with the same annotationId.
		start: (startAttributes: StartAttributes & { annotationId: string }) => {
			const experienceTracker = getExperienceTracker();
			selectInlineCommentCompoundExperience._start(startAttributes);

			const compoundExperienceState = experienceTracker.getExperienceState(
				SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
			);

			const skipStart = shouldSkipStarting({
				startAttributes,
				experienceAttributes: compoundExperienceState?.attributes,
			});
			// If the experience has already started for this annotation -- we don't start a new one
			// this can happen when the showComment is called after the selectComment
			// (ie. when the user selects an annotation in the document)
			if (skipStart) {
				return;
			}

			experienceTracker.start({
				name: SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
				attributes: commonSelectInlineCommentExperiencesAttributes,
				// If the comment is not opened in either the sidebar or panel within 10 seconds -- we assume there has been a failure to select the inline comment.
				timeout: 10000,
			});
			// The show experience can be started after the select experience
			// (ie. when the user selects an annotation in the document)
			// In this case we still want to track where the show experience started.
			selectInlineCommentCompoundExperience.addCommonAttributes({
				showCommentEntryPoint: startAttributes.entryPoint,
			});
			runningExperiences.push(SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE);
		},
		abort: ({
			abortReason,
		}: {
			abortReason:
				| 'Non inline comment being shown'
				| 'Comment sidebar dismissed'
				| 'InlineComment component unmounted'
				| 'InlineComment ui received new data and is hiding the inline comment ui'
				| 'Parent experience aborting with reason: test abort'
				| 'Parent experience aborting with reason: Comment being resolved'
				| 'Parent experience aborting with reason: Selected comment which has been resolved';
		}) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.abort({
				name: SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
				reason: abortReason,
			});
		},
		fail: (error: Error, extraAttributes?: { misalignedBy: number }) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.fail({
				name: SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
				error,
				attributes: { ...commonSelectInlineCommentExperiencesAttributes, ...extraAttributes },
			});
		},
		complete: (_completedFrom?: string) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.succeed({
				name: SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
				attributes: commonSelectInlineCommentExperiencesAttributes,
			});
		},
	},
};

/**
 * Checks if the experience should skip starting.
 *
 * Returns a string if the experience should skip starting,
 */
function shouldSkipStarting({
	startAttributes,
	experienceAttributes,
}: {
	startAttributes: StartAttributes;
	experienceAttributes: ExperienceAttributes | undefined;
}): 'running-experience-with-same-annotationId' | 'not-page-or-doc' | false {
	if (startAttributes.pageClass === 'not-page-or-doc') {
		return 'not-page-or-doc';
	}

	const hasAnnotationId = 'annotationId' in startAttributes;

	// For keyboard navigation and comment navigation, we don't know the annotationId when the experience starts.
	// These entry points should not be hit multiple times for the same selection so we don't need to skip here.
	if (!hasAnnotationId) {
		return false;
	}

	// If there is no running experience - we don't want to skip
	if (experienceAttributes === undefined) {
		return false;
	}

	// If the running experience has the same annotationId as the newly triggered one -- we want to skip.
	if (experienceAttributes?.annotationId === startAttributes.annotationId) {
		return 'running-experience-with-same-annotationId';
	}

	// In all other cases -- we don't want to skip
	return false;
}

const singleExperience_selectInlineCommentCompoundExperience = {
	_start: (startAttributes: StartAttributes, _options?: { annotationUnknown: true }) => {
		if (startAttributes.pageClass === 'not-page-or-doc') {
			return;
		}

		const experienceTracker = getExperienceTracker();
		const experienceState = experienceTracker.getExperienceState(SELECT_INLINE_COMMENT_EXPERIENCE);
		const runningExperienceAttributes = experienceState?.attributes;

		if (
			!experienceState?.hasStopped &&
			runningExperienceAttributes?.annotationId !== undefined &&
			runningExperienceAttributes?.annotationId === startAttributes.annotationId
		) {
			// If there is an existing (unstopped) experience
			// - and that experience has an annotationId
			//   - keyboard and comment navigation will start experiences without an annotationId -- and these should be restarted on an new start
			// - and that annotationId matches the one in the startAttributes
			// we assume the experience is still active, and don't start a new one.
			// the reason we can hit this is because the selectInlineCommentCompoundExperience._start is called when there is a "select inline comment"
			// signal, and individual signals fire in different orders (sometimes they are the first signal, sometimes they are later).

			// However -- if the second signal has a clearer entry point
			// we update the entry point attribute of the existing experience.
			if (
				(runningExperienceAttributes?.entryPoint as string | undefined)?.startsWith('unknown') &&
				!startAttributes.entryPoint.startsWith('unknown')
			) {
				selectInlineCommentCompoundExperience.addCommonAttributes({
					entryPoint: startAttributes.entryPoint,
				});
			}

			return;
		}

		if (
			// When the side bar is open, and the user selects a comment using the keyboard,
			// the keyboard navigation triggers a select annotation first (without an annotationId),
			// followed by the show comment start or select annotation complete (with an annotationId.
			startAttributes.entryPoint === 'unknown:CommentsPanelList' &&
			runningExperienceAttributes?.entryPoint === 'keyboard-navigation' &&
			runningExperienceAttributes?.annotationId === undefined
		) {
			selectInlineCommentCompoundExperience.addCommonAttributes({
				annotationId: startAttributes.annotationId,
			});

			return;
		}

		// First prepare the new attributes without modifying the global state
		// This ensures that any abort of current experiences will use their original attributes
		const newAttributes: ExperienceAttributes = {
			pageClass: startAttributes.pageClass,
			commentType: startAttributes.commentType,
			annotationId: 'annotationId' in startAttributes ? startAttributes.annotationId : undefined,
			entryPoint: startAttributes.entryPoint,
			version: 'single-experience',
		};

		// We check if there is a dom element with a testId of "object-sidebar-panel-body" and if so mark the
		// experience as being during a sidebar open experience.

		newAttributes.sidebarOpenAtStart =
			document.querySelector('[data-testid="object-sidebar-panel-body"]') !== null;

		newAttributes.experienceType = getExperienceType(
			startAttributes.entryPoint,
			newAttributes.sidebarOpenAtStart as boolean,
		);

		// If the create inline comment experience is running, we want to link the two experiences
		const createInlineCommentExperienceState = experienceTracker.getExperienceState(
			CREATE_INLINE_COMMENT_EXPERIENCE,
		);

		if (createInlineCommentExperienceState && !createInlineCommentExperienceState.hasStopped) {
			newAttributes.linkedCompoundTaskId =
				createInlineCommentExperienceState.attributes?.compoundTaskId;
		}

		// Adds a debug point to the last running experience (debug points are only added if there is a running experience)
		experienceTracker.attachDebugPoint({
			name: SELECT_INLINE_COMMENT_EXPERIENCE,
			message: `Restarted from ${startAttributes.entryPoint} with annotationId: ${startAttributes.annotationId} (sidebarOpenAtStart: ${newAttributes.sidebarOpenAtStart})`,
		});
		// Start press trace if no trace is currently active
		startUFOPressTrace(SELECT_INLINE_COMMENT_EXPERIENCE);

		// Then start a new compound experience with the new attributes
		// This will abort any running experiences with the same name, but they'll keep their original attributes
		experienceTracker.start({
			name: SELECT_INLINE_COMMENT_EXPERIENCE,
			attributes: newAttributes,
			// If the annotation is not selected and comment shown to the user within 12 seconds -- we assume there has been a failure to select the inline comment
			timeout: 30000,

			collect: (events) => {
				const isThisCommentBeingResolved = (event: ExperienceEvent) => {
					return (
						event.name === RESOLVE_INLINE_COMMENT_EXPERIENCE &&
						event.action === 'taskStart' &&
						hasAttribute(
							event,
							'annotationId',
							commonSelectInlineCommentExperiencesAttributes.annotationId,
						)
					);
				};
				events.forEach((event) => {
					if (isThisCommentBeingResolved(event)) {
						selectInlineCommentCompoundExperience.abort({
							abortReason: 'Comment being resolved',
						});
					}
				});
			},
		});

		if (startAttributes.annotationId === undefined) {
			experienceTracker.attachDebugPoint({
				name: SELECT_INLINE_COMMENT_EXPERIENCE,
				message: `selectInlineCommentCompoundExperience._start started from ${startAttributes.entryPoint} with unknown annotationId`,
			});
		}

		// Now it's safe to update the global state since any aborts have already happened
		// We reset experience state if we are starting a new experience
		commonSelectInlineCommentExperiencesAttributes = newAttributes;
	},
	addCommonAttributes: (additionalCommonAttributes: {
		[key: string]: boolean | null | number | string | undefined | Array<number | string>;
	}) => {
		const experienceTracker = getExperienceTracker();
		commonSelectInlineCommentExperiencesAttributes = {
			...commonSelectInlineCommentExperiencesAttributes,
			...additionalCommonAttributes,
		};

		experienceTracker.updateAttributes({
			name: SELECT_INLINE_COMMENT_EXPERIENCE,
			attributes: additionalCommonAttributes,
		});
	},
	abort: ({
		abortReason,
	}: {
		abortReason:
			| 'test abort'
			| 'Comment being resolved'
			| 'Selected comment which has been resolved';
	}) => {
		const experienceTracker = getExperienceTracker();
		experienceTracker.abort({
			name: SELECT_INLINE_COMMENT_EXPERIENCE,
			reason: abortReason,
			checkForTimeout: true,
		});
		commonSelectInlineCommentExperiencesAttributes = {};
	},
	selectAnnotation: {
		debug: getSelectInlineCommentExperienceDebug(
			SELECT_INLINE_COMMENT_EXPERIENCE,
			'selectAnnotation',
		),
		/**
		 * This is currently used to start from
		 * - annotationId unknown
		 *   - comment navigation
		 *   - keyboard navigation
		 * - annotationId known
		 *   - create comment
		 *   - comment sidebar comment selection
		 *
		 * These should be separate signals once the multi experience is deprecated.
		 */
		start: (
			startAttributes: AnnotationIdKnownStartAttributes | AnnotationIdUnknownStartAttributes,
		) => {
			function isUnknownAnnotationId(
				startAttributes: StartAttributes,
			): startAttributes is AnnotationIdKnownStartAttributes {
				return (
					startAttributes.entryPoint === 'keyboard-navigation' ||
					startAttributes.entryPoint === 'comment-navigation'
				);
			}

			if (isUnknownAnnotationId(startAttributes)) {
				singleExperience_selectInlineCommentCompoundExperience._start(startAttributes, {
					annotationUnknown: true,
					// there is no annotationId on the startAttributes for these entry points
				});

				return;
			}

			singleExperience_selectInlineCommentCompoundExperience._start(startAttributes);
		},
		abort: ({
			abortReason,
		}: {
			abortReason:
				| 'Comment navigation when only one comment'
				| 'Navigating to general comment'
				| 'Parent experience aborting with reason: test abort'
				| 'Parent experience aborting with reason: Comment being resolved'
				| 'Parent experience aborting with reason: Selected comment which has been resolved';
		}) => {
			singleExperience_selectInlineCommentCompoundExperience.abort({
				// @ts-ignore the abort reasons have mismatch due to type from the old multi experience. and once that is removed we can remove this cast
				abortReason,
			});
			commonSelectInlineCommentExperiencesAttributes = {};
		},
		/**
		 * Note -- the selectAnnotation does not trigger a completion of the selectInlineComment experience.
		 * Instead -- the selectInlineComment experience is completed when the showComment experience completes
		 * (which looks for the selected annotation as part of its completion logic).
		 *
		 * With the single experience -- this is less a complete and rather just an indirect signal that a selection has been made (in case it's not detected by the showComment experience).
		 * When the multi selectInlineCommentCompoundExperience is deprecated, this can be renamed to indicate it's a "annotationSelection" signal rather than a "completed" signal.
		 */
		complete: (startAttributes: StartAttributes & { annotationId: string }) => {
			const experienceTracker = getExperienceTracker();

			const experienceState = experienceTracker.getExperienceState(
				SELECT_INLINE_COMMENT_EXPERIENCE,
			);

			// There can be cases where no start has been called for the select experience.
			// - When a product consumer of the editor setselectedannotation api calls the select experience
			//   and has not been instrumented.
			// - When the annotation selection is via editor logic (ie. editor selection being moved onto
			//   an annotation).
			if (experienceState === null) {
				experienceTracker.attachDebugPoint({
					name: SELECT_INLINE_COMMENT_EXPERIENCE,
					message: 'selectAnnotation completed from no experience - no explicit start point',
					attributes: startAttributes,
				});

				singleExperience_selectInlineCommentCompoundExperience._start(startAttributes);
			}

			// Temporarily start the view-inline-comment experience here for the editor entry point
			startViewInlineCommentExperience(startAttributes);
		},
	},
	showComment: {
		debug: getSelectInlineCommentExperienceDebug(SELECT_INLINE_COMMENT_EXPERIENCE, 'showComment'),
		debugPoint: (message: string, attributes?: ExperienceAttributes) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.attachDebugPoint({
				name: SELECT_INLINE_COMMENT_EXPERIENCE,
				message,
				attributes,
			});
		},
		start: (startAttributes: StartAttributes & { annotationId: string }) => {
			const experienceTracker = getExperienceTracker();
			singleExperience_selectInlineCommentCompoundExperience._start(startAttributes);

			experienceTracker.attachDebugPoint({
				name: SELECT_INLINE_COMMENT_EXPERIENCE,
				message: 'showComment start triggered',
				attributes: startAttributes,
			});
		},
		abort: ({
			abortReason,
		}: {
			abortReason:
				| 'Non inline comment being shown'
				| 'Comment sidebar dismissed'
				| 'InlineComment component unmounted'
				| 'InlineComment ui received new data and is hiding the inline comment ui'
				| 'Parent experience aborting with reason: test abort'
				| 'Parent experience aborting with reason: Comment being resolved'
				| 'Parent experience aborting with reason: Selected comment which has been resolved';
		}) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.abort({
				name: SELECT_INLINE_COMMENT_EXPERIENCE,
				reason: abortReason,
			});
			commonSelectInlineCommentExperiencesAttributes = {};
		},
		fail: (error: Error, extraAttributes?: { misalignedBy: number }) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.fail({
				name: SELECT_INLINE_COMMENT_EXPERIENCE,
				error,
				attributes: { ...commonSelectInlineCommentExperiencesAttributes, ...extraAttributes },
			});
			commonSelectInlineCommentExperiencesAttributes = {};
		},
		complete: (completedFrom?: string) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.succeed({
				name: SELECT_INLINE_COMMENT_EXPERIENCE,
				attributes: { completedFrom, ...commonSelectInlineCommentExperiencesAttributes },
			});
			commonSelectInlineCommentExperiencesAttributes = {};
		},
	},
} satisfies typeof multiExperience_selectInlineCommentCompoundExperience;

/**
 * Based on a feature gate we want to use a distinct experience object.
 * Because feature gates can't be evaluated at the module level, we use
 * a proxy object to return the correct experience object based on the feature gate.
 *
 * If the feature gate is disabled, we use the `multiExperience_selectInlineCommentCompoundExperience`
 * other wise we use a new `singleExperience_selectInlineCommentCompoundExperience` (to be implemented).
 */
export const selectInlineCommentCompoundExperience = new Proxy(
	{} as typeof multiExperience_selectInlineCommentCompoundExperience,
	{
		get: (_target, prop: keyof typeof multiExperience_selectInlineCommentCompoundExperience) => {
			if (fg('cc_comments_split_select_comment_exp')) {
				return singleExperience_selectInlineCommentCompoundExperience[prop];
			}
			return multiExperience_selectInlineCommentCompoundExperience[prop];
		},
	},
);

const startViewInlineCommentExperience = (startAttributes: StartAttributes) => {
	const experienceTracker = getExperienceTracker();

	if (
		startAttributes.pageClass === 'editor' &&
		startAttributes.entryPoint === 'annotation-change' &&
		fg('confluence_frontend_view_inline_comment_editor')
	) {
		const experienceState = experienceTracker.getExperienceState(VIEW_INLINE_COMMENT_EXPERIENCE);

		if (
			experienceState === null ||
			experienceState?.hasStopped ||
			(experienceState?.hasStopped === false &&
				experienceState?.attributes?.entryPoint !== 'inline-comments-common/CommentNavigation' &&
				experienceState?.attributes?.entryPoint !==
					'annotation-provider-inline-comments/CreateComment')
		) {
			experienceTracker.start({
				name: VIEW_INLINE_COMMENT_EXPERIENCE,
				timeout: 30000,
				attributes: {
					entryPoint: 'experience-tracker/selectInlineCommentCompoundExperience',
				},
			});
		}
	}
};
